import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../contexts/AuthContext';
import Editor from '@monaco-editor/react';
import {
  Container,
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Paper,
  Grid,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Cancel,
  RemoveCircle,
  Code
} from '@mui/icons-material';
import axios from '../../../api/axios';
import UserAvatar from '../../../components/Avatar/UserAvatar';

// Helper function to get Monaco language from question language
const getMonacoLanguage = (lang) => {
  switch (lang) {
    case 'racket':
      return 'scheme';
    case 'python':
    default:
      return 'python';
  }
};

function StudentDetail() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { selectedCourse } = useContext(AuthContext);
  const [student, setStudent] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [codeDialogOpen, setCodeDialogOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [questionSubmissions, setQuestionSubmissions] = useState([]);

  useEffect(() => {
    fetchStudentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, selectedCourse]);

  const fetchStudentData = async () => {
    try {
      setLoading(true);
      
      if (!selectedCourse || !selectedCourse.id) {
        console.error('No course selected');
        setQuestions([]);
        return;
      }
      
      const courseTemplateId = selectedCourse.id;
      const [studentRes, questionsRes] = await Promise.all([
        axios.get(`/teacher/students/${studentId}`),
        axios.get('/questions', {
          params: { courseTemplateId }
        })
      ]);

      setStudent(studentRes.data.student);
      setSubmissions(studentRes.data.submissions);
      setQuestions(questionsRes.data);
    } catch (error) {
      console.error('Error fetching student data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get submission status for each question
  const getQuestionStatus = (questionId) => {
    const questionSubmissions = submissions.filter(s => s.questionId === questionId);
    if (questionSubmissions.length === 0) return null;
    
    // Find best submission
    const bestSubmission = questionSubmissions.reduce((best, current) => 
      current.score > best.score ? current : best
    );
    
    return {
      status: bestSubmission.status,
      score: bestSubmission.score,
      attempts: questionSubmissions.length,
      lastSubmitted: bestSubmission.submittedAt
    };
  };

  // Group questions by category
  const getQuestionsByCategory = () => {
    const categories = {};
    questions.forEach(q => {
      if (!categories[q.category]) {
        categories[q.category] = [];
      }
      categories[q.category].push({
        ...q,
        userStatus: getQuestionStatus(q._id)
      });
    });
    return categories;
  };

  const filteredCategories = () => {
    const all = getQuestionsByCategory();
    if (selectedCategory === 'all') return all;
    return { [selectedCategory]: all[selectedCategory] || [] };
  };

  // Calculate category stats
  const getCategoryStats = (categoryQuestions) => {
    const total = categoryQuestions.length;
    const attempted = categoryQuestions.filter(q => q.userStatus).length;
    const passed = categoryQuestions.filter(q => q.userStatus?.status === 'passed').length;
    const avgScore = attempted > 0
      ? Math.round(categoryQuestions.filter(q => q.userStatus).reduce((sum, q) => sum + (q.userStatus?.score || 0), 0) / attempted)
      : 0;
    
    return { total, attempted, passed, avgScore };
  };

  const getStatusIcon = (status) => {
    if (!status) return <RemoveCircle color="disabled" />;
    if (status.status === 'passed') return <CheckCircle color="success" />;
    return <Cancel color="error" />;
  };

  const handleViewCode = (question) => {
    const questionSubs = submissions.filter(s => s.questionId === question._id);
    setSelectedQuestion(question);
    setQuestionSubmissions(questionSubs);
    setCodeDialogOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'passed': return 'success';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return <LinearProgress />;
  }

  if (!student) {
    return (
      <Container>
        <Typography>学生未找到</Typography>
      </Container>
    );
  }

  const categorizedQuestions = filteredCategories();

  return (
    <Box>
      <AppBar position="static" elevation={1}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 2 }}>
            学生详情
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Student Info Card */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Stack direction="row" spacing={3} alignItems="center">
            <UserAvatar 
              user={student}
              size={80}
              sx={{ fontSize: '2rem' }}
            />
            <Box>
              <Typography variant="h4" fontWeight="bold">
                {student.name}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                手机号: {student.phoneNumber}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Category Filter */}
        <Stack direction="row" spacing={1} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
          <Chip
            label="全部"
            color={selectedCategory === 'all' ? 'primary' : 'default'}
            onClick={() => setSelectedCategory('all')}
            sx={{ cursor: 'pointer' }}
          />
          {Object.keys(getQuestionsByCategory()).map(cat => (
            <Chip
              key={cat}
              label={cat}
              color={selectedCategory === cat ? 'primary' : 'default'}
              variant={selectedCategory === cat ? 'filled' : 'outlined'}
              onClick={() => setSelectedCategory(cat)}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Stack>

        {/* Questions by Category */}
        {Object.entries(categorizedQuestions).map(([category, categoryQuestions]) => {
          const stats = getCategoryStats(categoryQuestions);
          
          return (
            <Box key={category} sx={{ mb: 4 }}>
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.50' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <Typography variant="h6" fontWeight="bold">
                      {category}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Typography variant="body2" color="text.secondary">
                      总题数: <strong>{stats.total}</strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Typography variant="body2" color="text.secondary">
                      已尝试: <strong>{stats.attempted}</strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Typography variant="body2" color="text.secondary">
                      已通过: <strong style={{ color: '#2e7d32' }}>{stats.passed}</strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" color="text.secondary">
                        平均分: <strong>{stats.avgScore}</strong>
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={stats.avgScore}
                        sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                        color={stats.avgScore >= 80 ? 'success' : stats.avgScore >= 60 ? 'warning' : 'error'}
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>

              <Card variant="outlined">
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width="80">状态</TableCell>
                        <TableCell>题目</TableCell>
                        <TableCell align="center" width="100">难度</TableCell>
                        <TableCell align="center" width="100">得分</TableCell>
                        <TableCell align="center" width="100">尝试次数</TableCell>
                        <TableCell width="150">最后提交</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {categoryQuestions.map(question => {
                        const status = question.userStatus;
                        return (
                          <TableRow 
                            key={question._id} 
                            hover
                            sx={{ cursor: status ? 'pointer' : 'default' }}
                            onClick={() => status && handleViewCode(question)}
                          >
                            <TableCell>
                              {getStatusIcon(status)}
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Chip label={question.questionNumber} size="small" variant="outlined" />
                                <Typography variant="body2" fontWeight="medium">
                                  {question.title}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={question.difficulty === 'easy' ? '简单' : question.difficulty === 'medium' ? '中等' : question.difficulty === 'exam' ? '考试原题' : '困难'}
                                size="small"
                                color={question.difficulty === 'easy' ? 'success' : question.difficulty === 'medium' ? 'warning' : question.difficulty === 'exam' ? 'info' : 'error'}
                              />
                            </TableCell>
                            <TableCell align="center">
                              {status ? (
                                <Typography 
                                  variant="body2" 
                                  fontWeight="bold"
                                  color={status.status === 'passed' ? 'success.main' : 'text.primary'}
                                >
                                  {status.score}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">-</Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {status ? (
                                <Chip label={status.attempts} size="small" />
                              ) : (
                                <Typography variant="body2" color="text.secondary">0</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {status ? (
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(status.lastSubmitted).toLocaleString('zh-CN', {
                                    month: '2-digit',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </Typography>
                              ) : (
                                <Typography variant="caption" color="text.secondary">-</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Box>
          );
        })}
      </Container>

      {/* Code View Dialog */}
      <Dialog open={codeDialogOpen} onClose={() => setCodeDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={2} alignItems="center">
            <Code />
            <Typography variant="h6">
              {selectedQuestion?.title} - {student.name}的提交记录
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {questionSubmissions.length === 0 ? (
            <Typography color="text.secondary">暂无提交记录</Typography>
          ) : (
            <Stack spacing={3}>
              {questionSubmissions.map((sub, index) => (
                <Box key={sub.id}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="subtitle2" fontWeight="bold">
                        提交 #{questionSubmissions.length - index}
                      </Typography>
                      <Chip
                        label={sub.status === 'passed' ? 'Accepted' : 'Wrong Answer'}
                        color={getStatusColor(sub.status)}
                        size="small"
                        icon={sub.status === 'passed' ? <CheckCircle /> : <Cancel />}
                      />
                      <Chip
                        label={`${sub.score}/100`}
                        size="small"
                        color={sub.status === 'passed' ? 'success' : 'default'}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(sub.submittedAt).toLocaleString('zh-CN')}
                    </Typography>
                  </Stack>

                  {sub.testResults && typeof sub.testResults === 'object' && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      测试通过: {sub.testResults.passed}/{sub.testResults.attempted}
                    </Typography>
                  )}

                  {/* Code Editor */}
                  <Box sx={{ 
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                    height: 400
                  }}>
                    <Editor
                      height="100%"
                      language={selectedQuestion ? getMonacoLanguage(selectedQuestion.language || 'python') : 'python'}
                      value={sub.code}
                      theme="light"
                      options={{
                        readOnly: true,
                        domReadOnly: true,
                        minimap: { enabled: false },
                        fontSize: 13,
                        lineNumbers: 'on',
                        scrollBeyondLastLine: false,
                        automaticLayout: true,
                        wordWrap: 'on',
                        fontFamily: "'Monaco', 'Menlo', 'Consolas', 'Courier New', monospace",
                      }}
                    />
                  </Box>

                  {sub.errorMessage && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        {sub.errorMessage}
                      </Typography>
                    </Alert>
                  )}

                  {index < questionSubmissions.length - 1 && <Divider sx={{ mt: 2 }} />}
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCodeDialogOpen(false)}>关闭</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default StudentDetail;

